import { searchCompanyDnB, searchCompanyDnBWithRegistration } from "../api/companyApi";

const clean = (value) => String(value || "").trim();
const normalized = (value) => clean(value).toLowerCase().replace(/\b(incorporated|corporation|company|limited|llc|ltd|inc|corp|plc)\b/g, " ").replace(/[^a-z0-9]+/g, " ").trim();
const tokens = (value) => new Set(normalized(value).split(" ").filter(Boolean));

function nameScore(left, right) {
  const a = normalized(left), b = normalized(right);
  if (!a || !b) return 0;
  if (a === b) return 60;
  if ((a.length >= 5 && b.includes(a)) || (b.length >= 5 && a.includes(b))) return 52;
  const at = tokens(a), bt = tokens(b);
  const shared = [...at].filter((token) => bt.has(token)).length;
  const union = new Set([...at, ...bt]).size || 1;
  return Math.round(50 * shared / union);
}

const addressText = (value) => typeof value === "string" ? value : [
  value?.streetAddressLine1, value?.streetAddressLine2, value?.addressLocality?.name,
  value?.addressRegion?.name, value?.postalCode, value?.addressCountry?.name,
].filter(Boolean).join(" ");

export function normalizeDnbCandidates(response) {
  const payload = response?.success !== undefined ? response.data : response;
  const rows = Array.isArray(payload) ? payload : (payload?.searchCandidates || payload?.matchCandidates || []);
  return rows.map((row) => row?.organization || row).filter(Boolean).map((row) => ({
    id: clean(row.duns || row.id) || null,
    dunsNumber: clean(row.duns || row.dunsNumber) || null,
    registrationNumber: clean(row.registrationNumber || row.primaryRegistrationNumber) || null,
    name: clean(row.primaryName || row.name || row.companyName),
    country: clean(row.primaryAddress?.addressCountry?.name || row.country),
    address: addressText(row.primaryAddress || row.address),
    website: clean(row.domain || row.website),
    source: "dnb",
  })).filter((row) => row.name);
}

function candidateScore(entity, candidate) {
  const reasons = [];
  const inputDuns = clean(entity?.dunsNumber || entity?.duns_number);
  const inputRegistration = clean(entity?.registrationNumber || entity?.registration_number);
  if (inputDuns && candidate.dunsNumber && inputDuns === clean(candidate.dunsNumber)) return { score: 100, reasons: ["CrediArc company identifier is an exact match"] };
  if (inputRegistration && candidate.registrationNumber && normalized(inputRegistration) === normalized(candidate.registrationNumber)) return { score: 100, reasons: ["Registration number is an exact match"] };

  let score = nameScore(entity?.name, candidate.name);
  if (score >= 52) reasons.push(score === 60 ? "Legal name is an exact match" : "Legal names strongly overlap");
  else if (score) reasons.push("Legal name partially overlaps");
  if (score === 60 && candidate.isClientEntity) { score += 25; reasons.push("Matches the assigned client entity"); }

  const inputCountry = normalized(entity?.country || entity?.countryName || entity?.iso2Code);
  const candidateCountry = normalized(candidate.country);
  if (inputCountry && candidateCountry && (inputCountry === candidateCountry || inputCountry.includes(candidateCountry) || candidateCountry.includes(inputCountry))) {
    score += 15; reasons.push("Country matches");
  }
  const inputAddress = normalized([entity?.address, entity?.city, entity?.state, entity?.postalCode || entity?.postal_code].filter(Boolean).join(" "));
  const candidateAddress = normalized(candidate.address);
  if (inputAddress && candidateAddress) {
    const overlap = [...tokens(inputAddress)].filter((token) => tokens(candidateAddress).has(token)).length;
    if (overlap >= 3) { score += 15; reasons.push("Address details match"); }
    else if (overlap >= 1) { score += 6; reasons.push("Some address details match"); }
  }
  const domain = (value) => clean(value).toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  if (domain(entity?.website) && domain(entity?.website) === domain(candidate.website)) { score += 10; reasons.push("Website domain matches"); }
  return { score: Math.min(100, score), reasons };
}

const localCandidate = (row) => ({
  id: row.companyId || row.id || null,
  dunsNumber: row.dunsNumber || row.dnbCompanyId || null,
  registrationNumber: row.registrationNumber || null,
  name: row.name || row.companyName || "",
  country: row.country || row.region || "",
  address: row.address || "",
  website: row.website || "",
  source: "portfolio",
  isClientEntity: Boolean(row.isClientEntity),
});

export async function matchApplicationEntity(entity, { tenantId, portfolioRows = [], searchByName = searchCompanyDnB, searchByRegistration = searchCompanyDnBWithRegistration } = {}) {
  const inputName = clean(entity?.name);
  if (!inputName) return { inputName: "", id: null, name: "", dunsNumber: null, country: null, score: 0, reasoning: "No entity name was extracted", source: "unmatched" };
  let dnb = [];
  try {
    const registrationNumber = clean(entity?.registrationNumber || entity?.registration_number);
    const response = registrationNumber
      ? await searchByRegistration({ tenantId, registrationNumber, iso2Code: entity?.iso2Code || "" })
      : await searchByName({ name: inputName, tenantId, iso2Code: entity?.iso2Code || "", countryName: entity?.country || "" });
    dnb = normalizeDnbCandidates(response);
  } catch (error) {
    // Portfolio matching remains available if the external registry is disabled or unavailable.
  }
  const candidates = [...portfolioRows.map(localCandidate), ...dnb].filter((row) => row.name);
  if (!candidates.length) return { inputName, id: null, name: inputName, dunsNumber: entity?.dunsNumber || null, country: entity?.country || null, score: 0, reasoning: "No portfolio or CrediArc company-search candidates were found", source: "unmatched" };
  const ranked = candidates.map((candidate) => ({ candidate, ...candidateScore(entity, candidate) })).sort((a, b) => b.score - a.score);
  const best = ranked[0], confident = best.score >= 75;
  return {
    inputName,
    id: confident ? best.candidate.id : null,
    name: confident ? best.candidate.name : inputName,
    dunsNumber: confident ? best.candidate.dunsNumber : (entity?.dunsNumber || null),
    country: confident ? best.candidate.country : (entity?.country || null),
    score: best.score,
    reasoning: best.reasons.length ? best.reasons.join("; ") : "Candidate identifiers did not overlap sufficiently",
    source: confident ? best.candidate.source : "unmatched",
  };
}

export function applicationCompleteness(extraction) {
  const missing = Array.isArray(extraction?.missing_required_fields) ? extraction.missing_required_fields : [];
  const supplied = Object.values(extraction?.answers || {}).filter((value) => value !== null && value !== undefined && clean(value) !== "").length;
  if (!supplied && !missing.length) return 0;
  return Math.round(100 * supplied / Math.max(1, supplied + missing.length));
}
